/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useNavigate, useParams } from 'react-router-dom'
import { useEffect } from 'react'

import type { Params } from 'react-router-dom'
import type React from 'react'

type Props = {
  to: string | ((params: Params) => string)
  replace?: false
}

/**
 * Custom redirect component. Use instead of `<Navigate />` (which only works
 * for static paths when invoked directly in a route object) or `loader` (which
 * doesn't play nicely with our integration tests).
 *
 * @example
 *
 *   {
 *     path: '/abc'
 *     element: <Redirect to='/def' />
 *   },
 *   {
 *     path: '/abc/:someId'
 *     element: <Redirect to={{{someId}) => `/def/${someId}`} />
 *   },
 */
const Redirect: React.FunctionComponent<Props> = ({ to, replace = true }) => {
  const navigate = useNavigate()
  const params = useParams()

  const url = typeof to === 'function' ? to(params) : to

  useEffect(() => {
    navigate(url, { replace })
  }, [])

  return null
}

export default Redirect
